'use no memo';
import type {PointerEvent, ReactNode} from 'react';
import {useEffect} from 'react';
import {useRef, useState} from 'react';
import {CUSTOM_ROTATE_CONTROL_MARGIN} from './custom-rotation-control.types';
import {CUSTOM_ROTATION_CONTROL_ID} from './custom-rotation-control.types';
import {getAngleBetweenFabricObjectCenterAndCursor} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {degreesToRadians} from '@Utils/math.util';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {
  CUSTOM_CONTROLS_WRAPPER,
  CUSTOM_ITEM_CONTROL_BORDER,
  CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE,
  CUSTOM_ITEM_DIMENSIONS_CONTROL_LONGER_SIDE,
  CustomControlCursor,
} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import {useAppSelector} from '@/hooks';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';

export function  CustomRotationControl(props: CustomItemControlsProps): ReactNode | null {
  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });

  const [isActive, setIsActive] = useState(false);
  const initialClientX = useRef<number | null>(null);
  const initialClientY = useRef<number | null>(null);

  const getHorizontalCorrection = (fabricObject: FabricObject): number => {
    const controlsWrapperSelector = `.${CUSTOM_CONTROLS_WRAPPER}#${CSS.escape(props.pageHashedId)}`;
    const controlsWrapper = document.querySelector<HTMLElement>(controlsWrapperSelector);
    if (!controlsWrapper) {
      return fabricObject.canvas?._offset.left ?? 0;
    }

    const wrapperRect = controlsWrapper.getBoundingClientRect();

    return wrapperRect.left;
  };

  const getVerticalCorrection = (fabricObject: FabricObject): number => {
    const controlsWrapperSelector = `.${CUSTOM_CONTROLS_WRAPPER}#${CSS.escape(props.pageHashedId)}`;
    const controlsWrapper = document.querySelector<HTMLElement>(controlsWrapperSelector);

    if (!controlsWrapper) {
      return fabricObject.canvas?._offset.top ?? 0;
    }

    const wrapperRect = controlsWrapper.getBoundingClientRect();

    return wrapperRect.top;
  };

  const handlePointerUp = (): void => {
    if (!isActive) {
      return;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    void onRotateSelectionEnded();

    setIsActive(false);

    page.setPosterItemsModificationState(false);

    initialClientX.current = null;
    initialClientY.current = null;
  };

  const handlePointerMove = (e: globalThis.PointerEvent): void => {
    if (!isActive) {
      return;
    }

    onRotateSelection(e);
  };

  const handlePointerDown = (e: PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    const target = e.target as HTMLElement;
    if (target.id === CUSTOM_ROTATION_CONTROL_ID) {
      setIsActive(true);

      initialClientX.current = e.clientX;
      initialClientY.current = e.clientY;

      page.setPosterItemsModificationState('rotating');
    }
  };

  const getNewRotationAngle = (angleBetweenFabricObjectCenterAndCursor: number): number => {
    let angle = angleBetweenFabricObjectCenterAndCursor;

    if (angleBetweenFabricObjectCenterAndCursor < 0) {
      angle = angleBetweenFabricObjectCenterAndCursor + 360;
    }

    const targetAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];

    for (const targetAngle of targetAngles) {
      if (Math.abs(angle - targetAngle) <= 2) {
        return targetAngle;
      }
    }

    return angle;
  };

  const onRotateSelectionEnded = async (): Promise<void> => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    const updateFromObjectPromises = [];

    if (activeSelection instanceof ActiveSelection) {
      const fabricObjects = activeSelection.getObjects();
      for (const fabricObject of fabricObjects) {
        const item = page.items.getItemForFabricObject(fabricObject);
        if (item) {
          updateFromObjectPromises.push(
            item.updateFromObject(
              {
                left: item.fabricObject.left,
                top: item.fabricObject.top,
                angle: item.fabricObject.angle,
              },
              {undoable: false}
            )
          );
        }
      }
      const groupId = page.items.groups.getGroupIdForFabricObjects(fabricObjects);
      if (groupId) {
        page.items.groups.updateGroup(groupId, {angle: activeSelection.angle});
      }
    } else {
      const item = page.items.getItemForFabricObject(activeSelection);
      if (item) {
        updateFromObjectPromises.push(
          item.updateFromObject(
            {
              left: item.fabricObject.getX(),
              top: item.fabricObject.getY(),
              angle: item.fabricObject.angle,
            },
            {undoable: false}
          )
        );
      }
    }

    await Promise.all(updateFromObjectPromises);

    page.poster.history.addPosterHistory();
  };

  const onRotateSelection = (e: globalThis.PointerEvent): void => {
    if (initialClientX.current === null || initialClientY.current === null) {
      return;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    const angleBetweenFabricObjectCenterAndCursor = getAngleBetweenFabricObjectCenterAndCursor(
      e,
      activeSelection,
      page,
      getHorizontalCorrection(activeSelection),
      getVerticalCorrection(activeSelection)
    );

    const newRotationAngle = getNewRotationAngle(angleBetweenFabricObjectCenterAndCursor);

    activeSelection.rotate(newRotationAngle);
    activeSelection.setCoords();

    if (activeSelection instanceof ActiveSelection) {
      const fabricObjects = activeSelection.getObjects();
      for (const fabricObject of fabricObjects) {
        const item = page.items.getItemForFabricObject(fabricObject);
        if (item) {
          item.onRotating();
        }
      }
    } else {
      const item = page.items.getItemForFabricObject(activeSelection);
      if (item) {
        item.onRotating();
      }
    }

    if (angleBetweenFabricObjectCenterAndCursor !== newRotationAngle) {
      updateInitialRefs(e);
    }

    page.fabricCanvas.requestRenderAll();
  };

  const updateInitialRefs = (e: globalThis.PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    initialClientX.current = e.clientX;
    initialClientY.current = e.clientY;
  };

  const getCustomRotationControlPosition = (): CustomItemControlPositions => {
    const itemWidth = (props.positionProperties.width * props.positionProperties.scaleX + props.positionProperties.strokeWidth) * props.positionProperties.posterScale;

    const leftInitial = props.positionProperties.left * props.positionProperties.posterScale;
    const topInitial = props.positionProperties.top * props.positionProperties.posterScale;

    const xCorrectionDueToItemWidth = (itemWidth / 2) * Math.cos(degreesToRadians(props.positionProperties.angle));
    const yCorrectionDueToItemWidth = (itemWidth / 2) * Math.sin(degreesToRadians(props.positionProperties.angle));

    const x = CUSTOM_ROTATE_CONTROL_MARGIN * Math.sin(degreesToRadians(props.positionProperties.angle));
    const y = CUSTOM_ROTATE_CONTROL_MARGIN * Math.cos(degreesToRadians(props.positionProperties.angle));

    const left =
      leftInitial +
      xCorrectionDueToItemWidth -
      Math.ceil(CUSTOM_ITEM_DIMENSIONS_CONTROL_LONGER_SIDE / 2 - x) +
      CUSTOM_ITEM_CONTROL_BORDER * Math.cos(degreesToRadians(props.positionProperties.angle));
    const top = topInitial + yCorrectionDueToItemWidth - Math.ceil(CUSTOM_ITEM_DIMENSIONS_CONTROL_LONGER_SIDE / 2) - y;

    return {left, top, angle: props.positionProperties.angle};
  };

  const getRotationControl = (): ReactNode | null => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return null;
    }

    if (activeSelection.lockRotation) {
      return null;
    }

    const positionAndSize = getCustomRotationControlPosition();

    return (
      <BaseCustomItemControl
        icon={'icon-rotate-ccw'}
        controlId={CUSTOM_ROTATION_CONTROL_ID}
        top={positionAndSize.top}
        left={positionAndSize.left}
        width={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
        height={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
        angle={positionAndSize.angle}
        borderRadius={'50%'}
        isVisible={(!arePosterItemsMoving && !arePosterItemsRotating && !arePosterItemsScaling) || (arePosterItemsRotating && isActive)}
        onPointerDown={handlePointerDown}
        cursor={CustomControlCursor.CURSOR_CROSSHAIR}
      />
    );
  };

  useEffect(() => {
    if (isActive) {
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointermove', handlePointerMove);
    }

    return (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isActive]);

  if (arePosterItemsScaling) {
    return null;
  }

  if (arePosterItemsMoving || arePosterItemsScaling || (arePosterItemsRotating && !isActive)) {
    return null;
  }

  return getRotationControl();
}
